/**
 * Typed API module for per-project MCP server management (Phase 1).
 *
 * Mirrors the frozen backend contract in backend/models/mcp_models.py.
 * Secrets are WRITE-ONLY: responses always mask header values with
 * MASKED_SECRET; sending MASKED_SECRET back on update keeps the stored value.
 */

import api from '../utils/axios';

export const MASKED_SECRET = '********';

export type MCPTransport = 'streamable_http' | 'sse';

export type MCPSharingMode = 'shared' | 'individual';

export type MCPServerStatus =
  | 'unconfigured'
  | 'testing'
  | 'auth_required'
  | 'connected'
  | 'error';

export type MCPTestErrorKind =
  | 'unreachable'
  | 'credentials_rejected'
  | 'protocol_error'
  | 'timeout'
  | 'blocked_url'
  | 'unknown';

export interface MCPOAuthParams {
  /** Null/omitted = register automatically via DCR on Sign in (when supported). */
  client_id?: string | null;
  client_secret?: string | null;
  scopes: string[];
  authorization_endpoint?: string | null;
  token_endpoint?: string | null;
}

export interface MCPOAuthResponse {
  /** Null until a client exists (entered manually or DCR-registered on Sign in). */
  client_id?: string | null;
  /** A client_id exists (user-provided or DCR-registered). */
  registered: boolean;
  has_client_secret: boolean;
  scopes: string[];
  authorization_endpoint?: string | null;
  token_endpoint?: string | null;
  has_oauth_tokens: boolean;
  /** Access-token expiry as a Unix epoch timestamp (seconds). */
  expires_at?: number | null;
}

export interface MCPToolSummary {
  name: string;
  description?: string | null;
}

export interface MCPServer {
  id: string;
  project_id: string;
  name: string;
  description?: string | null;
  transport: MCPTransport;
  url: string;
  /** shared = one credential set for the whole project; individual = per-user creds */
  sharing_mode: MCPSharingMode;
  /** Whether YOU (the requesting user) have configured/authorized this server. */
  configured: boolean;
  /** header name -> MASKED_SECRET (values are never returned) */
  custom_headers: Record<string, string>;
  oauth?: MCPOAuthResponse | null;
  enabled: boolean;
  status: MCPServerStatus;
  last_error?: string | null;
  last_checked_at?: string | null;
  tool_count?: number | null;
  tools: MCPToolSummary[];
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MCPServerListResponse {
  servers: MCPServer[];
  total_count: number;
}

export interface MCPToolPreview {
  name: string;
  prefixed_name?: string | null;
  description?: string | null;
  input_schema?: Record<string, unknown> | null;
}

export interface MCPTestConnectionResult {
  success: boolean;
  status: MCPServerStatus;
  error_kind?: MCPTestErrorKind | null;
  error_detail?: string | null;
  hint?: string | null;
  tools: MCPToolPreview[];
  server_name?: string | null;
  tested_at: string;
}

export interface MCPOAuthStartResult {
  authorization_url: string;
}

export interface MCPOAuthConfig {
  redirect_url: string;
}

export interface MCPServerCreateRequest {
  name: string;
  description?: string;
  transport: MCPTransport;
  url: string;
  custom_headers: Record<string, string>;
  oauth?: MCPOAuthParams | null;
  sharing_mode?: MCPSharingMode;
  /** A project holds at most one server per (URL host, sharing mode). A
   * same-mode duplicate answers 409 (code `mcp_server_conflict`); after the
   * user confirms, re-send the same payload with replace=true to patch the
   * existing server in place and reset its credentials. */
  replace?: boolean;
}

/** The URL is immutable after creation — replacing a server goes through the
 * create + confirm-replace flow instead. */
export interface MCPServerUpdateRequest {
  name?: string;
  description?: string;
  transport?: MCPTransport;
  /** Full desired mapping: MASKED_SECRET keeps stored value, omitted keys are deleted */
  custom_headers?: Record<string, string>;
  oauth?: MCPOAuthParams | null;
  clear_oauth?: boolean;
  /** Enable/disable the server without affecting its last-tested status */
  enabled?: boolean;
}

const base = (projectId: string) => `/projects/${projectId}/mcp-servers`;

export const mcpApi = {
  async listServers(projectId: string): Promise<MCPServerListResponse> {
    const response = await api.get(base(projectId));
    return response.data;
  },

  async createServer(projectId: string, request: MCPServerCreateRequest): Promise<MCPServer> {
    const response = await api.post(base(projectId), request);
    return response.data;
  },

  async updateServer(
    projectId: string,
    serverId: string,
    request: MCPServerUpdateRequest
  ): Promise<MCPServer> {
    const response = await api.patch(`${base(projectId)}/${serverId}`, request);
    return response.data;
  },

  /** Enable/disable is just a PATCH with the `enabled` flag. */
  async toggleServer(projectId: string, serverId: string, enabled: boolean): Promise<MCPServer> {
    return this.updateServer(projectId, serverId, { enabled });
  },

  async deleteServer(projectId: string, serverId: string): Promise<void> {
    await api.delete(`${base(projectId)}/${serverId}`);
  },

  /** Remove only YOUR credentials for this server (sign-in / headers). */
  async disconnectCredentials(projectId: string, serverId: string): Promise<MCPServer> {
    const response = await api.delete(`${base(projectId)}/${serverId}/credentials`);
    return response.data;
  },

  async testServer(projectId: string, serverId: string): Promise<MCPTestConnectionResult> {
    const response = await api.post(`${base(projectId)}/${serverId}/test`);
    return response.data;
  },

  /** Begin OAuth: returns the provider consent URL to open in a popup. */
  async startOAuth(projectId: string, serverId: string): Promise<MCPOAuthStartResult> {
    const response = await api.post(`${base(projectId)}/${serverId}/oauth/start`);
    return response.data;
  },

  /** The redirect URL the user must register with their OAuth provider. */
  async getOAuthConfig(): Promise<MCPOAuthConfig> {
    const response = await api.get('/mcp/oauth/config');
    return response.data;
  },
};

export default mcpApi;
